'use client'
import type { FC } from 'react'
import { HStack, Icon, VStack } from '@chakra-ui/react'
import { BiCreditCard, BiReceipt } from 'react-icons/bi'
import { GrandRadioCard } from 'ui'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import type { OrderMode } from '../context/types'

export interface OrderModeSelectOverrides {
  orderModeUpfrontLabel?: string
  orderModeUpfrontDescription?: string
  orderModeInvoiceLabel?: string
  orderModeInvoiceDescription?: string
  orderModeInvoiceDescriptionProforma?: string
}

interface OrderModeSelectProps {
  value: OrderMode
  onChange: (mode: OrderMode) => void
  /** True when the company's sales terms resolve to PROFORMA — the invoice
   *  option then advertises prepayment instead of pay-after-delivery. */
  isProforma?: boolean
}

/**
 * Card / invoice switcher shown on the checkout summary to shoppers whose
 * company sales terms allow invoice billing. Built on `GrandRadioCard` — the
 * same card primitive the delivery selector uses — so the selected/hover
 * styling comes from the shared variant instead of ad-hoc colors.
 */
const OrderModeSelect: FC<OrderModeSelectProps> = ({ value, onChange, isProforma }) => {
  const overrides = useOverridesContext<keyof OrderModeSelectOverrides>()

  const options: {
    id: OrderMode
    icon: typeof BiCreditCard
    label: string
    description: string
  }[] = [
    {
      id: 'UPFRONT',
      icon: BiCreditCard,
      label: overrideText('Betala direkt', overrides?.orderModeUpfrontLabel),
      description: overrideText(
        'Betala din beställning direkt i kassan.',
        overrides?.orderModeUpfrontDescription
      )
    },
    {
      id: 'INVOICE',
      icon: BiReceipt,
      label: overrideText('Faktura', overrides?.orderModeInvoiceLabel),
      description: isProforma
        ? overrideText(
            'Proformafaktura – betalning krävs innan leverans.',
            overrides?.orderModeInvoiceDescriptionProforma
          )
        : overrideText(
            'Faktureras till företaget efter beställning.',
            overrides?.orderModeInvoiceDescription
          )
    }
  ]

  return (
    <GrandRadioCard.Root
      onChange={(next) => onChange(next as OrderMode)}
      rootProps={{ w: 'full' }}
      size="md"
      value={value}
      variant="outline"
    >
      <VStack align="stretch" spacing={3} w="full">
        {options.map(({ id, icon: OptionIcon, label, description }) => (
          <GrandRadioCard.Item
            key={id}
            aria-label={`order-mode-${id.toLowerCase()}`}
            value={id}
            w="full"
          >
            <HStack alignItems="center" justify="space-between" gap={4}>
              <HStack alignItems="center" gap={3}>
                <GrandRadioCard.Indicator />
                <GrandRadioCard.ItemContent>
                  <GrandRadioCard.ItemText>{label}</GrandRadioCard.ItemText>
                  <GrandRadioCard.ItemDescription>{description}</GrandRadioCard.ItemDescription>
                </GrandRadioCard.ItemContent>
              </HStack>
              <Icon as={OptionIcon} boxSize={5} />
            </HStack>
          </GrandRadioCard.Item>
        ))}
      </VStack>
    </GrandRadioCard.Root>
  )
}

export default OrderModeSelect
